import { useMutation, useQueryClient } from '@tanstack/react-query'
import { signOut } from 'aws-amplify/auth'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { clearTokens } from 'src/lib/token-provider'
import { sendEvent } from 'src/shared/api/events/send-event'

const consumerLoginPath = paths.consumer.auth.login.getHref()
const enterpriseLoginPath = paths.enterprise.login.getHref()
const professionalLoginPath = paths.auth.login.getHref()

interface UseSignOutProps {
  isConsumer?: boolean
  isEnterprise?: boolean
  isProfessional?: boolean
  preventNavigate?: boolean
}

export const useSignOut = ({
  isConsumer = false,
  isEnterprise = false,
  isProfessional = false,
  preventNavigate = false,
}: UseSignOutProps = {}) => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const signOutNavigate = () => {
    if (isConsumer) {
      navigate(consumerLoginPath)
    } else if (isEnterprise) {
      navigate(enterpriseLoginPath)
    } else if (isProfessional) {
      navigate(professionalLoginPath)
    } else {
      navigate(paths.root.getHref())
    }
  }

  return useMutation({
    mutationFn: async () => {
      // Clear custom tokens (for consumer auth via backend)
      clearTokens()
      // Sign out from Amplify
      await signOut()
    },
    onSuccess: () => {
      // Clear all query cache data to prevent stale data issues
      queryClient.clear()
      // Trigger navigation through state change
      if (!preventNavigate) {
        signOutNavigate()
      }
      sendEvent({ eventType: 'auth_signout' })
    },
    onError: (error: unknown) => {
      const errorName = error instanceof Error ? error.name : 'unknown'
      sendEvent({
        eventType: 'error',
        info: { error: errorName },
      })
    },
  })
}
